import { topicName } from '@/constants/topics'
import { createAdminClient } from '@/lib/supabase/admin'
import { storyUrl } from '@/utils/date'

// Site search (2026-10-02): one ranked list across storylines, stories and
// sources (and topics, the people, organizations and places in stories, since
// 2026-10-15), from the search_site() Postgres function (pipeline migration
// 20261002120000_site_search). Full-text match on what we already store plus
// trigram similarity on names, so no model call and no extra service. The
// function returns one row shape for every kind; this turns each row into
// what a list row needs, including its href.

export const MIN_QUERY = 2
export const MAX_QUERY = 100
export const KINDS = ['entity', 'storyline', 'story', 'source']

export function cleanQuery(raw) {
  const q = String(raw || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_QUERY)
  return q.length >= MIN_QUERY ? q : ''
}

function toResult(row) {
  const base = {
    kind: row.kind,
    slug: row.slug,
    title: row.title,
    score: row.score,
  }
  if (row.kind === 'story') {
    return {
      ...base,
      href: storyUrl({
        category: row.category,
        published_date: row.published_date,
        slug: row.slug,
      }),
      subtitle: row.subtitle || '',
      category: row.category,
      date: row.published_date,
      figure: row.figure || 0,
      unit: row.figure === 1 ? 'article' : 'articles',
    }
  }
  if (row.kind === 'storyline') {
    return {
      ...base,
      href: `/storyline/${row.slug}`,
      date: row.published_date,
      figure: row.figure || 0,
      unit: row.figure === 1 ? 'chapter' : 'chapters',
    }
  }
  if (row.kind === 'entity') {
    return {
      ...base,
      title: topicName(row.title, row.category),
      href: `/topic/${row.slug}`,
      subtitle: row.subtitle || '',
      image: row.image,
      figure: row.figure || 0,
      unit: row.figure === 1 ? 'story' : 'stories',
    }
  }
  if (row.kind === 'source') {
    return {
      ...base,
      href: `/sources/${row.slug}`,
      bias: row.bias,
      reliability: row.reliability,
      image: row.image,
    }
  }
  return null
}

// { query, results } where results are in rank order across kinds. An
// invalid or too-short query returns no results without touching the DB.
// `since` (a YYYY-MM-DD or ISO date) keeps stories published, and storylines
// updated, on or after it (search_site v3, 2026-10-27); topics and sources
// are unaffected.
export async function searchSite(rawQuery, { limit = 20, since = null } = {}) {
  const query = cleanQuery(rawQuery)
  if (!query) return { query, results: [] }

  const supabase = createAdminClient()
  const { data, error } = await supabase.rpc('search_site', {
    q: query,
    max_results: Math.max(1, Math.min(100, limit)),
    ...(since ? { since } : {}),
  })
  if (error) throw new Error(`search_site failed: ${error.message}`)

  const results = (data || []).map(toResult).filter((r) => r && r.href)
  // search_site() ranks every topic and storyline above every story, then
  // cuts at max_results, so a common name ("trump": 17 topics, 43
  // storylines) left no room for a single story. When a full answer has no
  // stories, ask for stories alone, newest first (2026-11-01).
  if (results.length >= limit && !results.some((r) => r.kind === 'story'))
    results.push(...(await storiesOnly(supabase, query, since)))
  await addStoryLean(supabase, results)
  return { query, results }
}

const STORIES_ONLY = 10

async function storiesOnly(supabase, query, since) {
  try {
    let q = supabase
      .from('topics')
      .select('slug, title, byline, category, published_date')
      .textSearch('search_tsv', query, { type: 'websearch', config: 'english' })
      .order('published_date', { ascending: false })
      .limit(STORIES_ONLY)
    if (since) q = q.gte('published_date', since)
    const { data } = await q
    return (data || [])
      .map((t) =>
        toResult({
          kind: 'story',
          slug: t.slug,
          title: t.title,
          subtitle: t.byline,
          category: t.category,
          published_date: t.published_date,
          figure: 0,
          score: 0,
        })
      )
      .filter((r) => r && r.href)
  } catch (err) {
    console.error('Search stories-only lookup failed:', err)
    return []
  }
}

// Each story result's lean (2026-11-01): the label the pipeline stores and
// the counts its page was written from, so the search box can draw the
// story's bar and label the way every list row does. One query for all the
// stories in the answer. Fail-open: a miss leaves the rows without a bar.
async function addStoryLean(supabase, results) {
  const stories = results.filter((r) => r.kind === 'story')
  if (!stories.length) return
  try {
    const { data } = await supabase
      .from('topics')
      .select(
        'slug, coverage_label, coverage_color, counts:story_format->coverage_counts'
      )
      .in(
        'slug',
        stories.map((r) => r.slug)
      )
    const bySlug = new Map((data || []).map((t) => [t.slug, t]))
    for (const r of stories) {
      const t = bySlug.get(r.slug)
      if (!t) continue
      r.coverage_label = t.coverage_label || null
      r.coverage_color = t.coverage_color || null
      if (t.counts && typeof t.counts === 'object')
        r.lean = {
          left: t.counts.left || 0,
          center: t.counts.center || 0,
          right: t.counts.right || 0,
        }
    }
  } catch (err) {
    console.error('Search lean lookup failed:', err)
  }
}

// Results split by kind, each list still in rank order.
export function groupResults(results) {
  const groups = { entity: [], storyline: [], story: [], source: [] }
  for (const r of results || []) if (groups[r.kind]) groups[r.kind].push(r)
  return groups
}

// The kinds that have results, best match first: the kind whose top row
// scores highest leads, so "fox" opens with the sources and "iran" with the
// storyline. Rows keep their rank order inside each kind.
export function orderKinds(groups) {
  return KINDS.filter((k) => groups[k]?.length).sort(
    (a, b) => (groups[b][0].score || 0) - (groups[a][0].score || 0)
  )
}

// Entities (people, organizations, places) read as topics: their page
// (/topic/<slug>) is who they are and every story they're in.
export const KIND_LABELS = {
  entity: { one: 'Topic', many: 'Topics' },
  storyline: { one: 'Storyline', many: 'Storylines' },
  story: { one: 'Story', many: 'Stories' },
  source: { one: 'Outlet', many: 'Outlets' },
}
